import { useMemo, useCallback } from 'react';
import { UserProject } from '../types/project.types';
import { AUTHENTICATION_PROVIDER, AuthType } from '../types';
import {
	selectProjects,
	selectTeams,
	selectAuthenticationContext,
} from '../selectors/authenticationContext.selectors';
import { useSelector, useDispatch } from 'react-redux';
import {
	getProjects,
	injectProject,
} from '../actions/authenticationContext.actions';
import { useIsFeatureEnabled } from '../utils';
import { useHistory } from 'react-router-dom';
import { setTenantId } from '@innovyze/lib_get_service';
import { AxiosError } from 'axios';

interface UseProjects {
	/**
	 * Whether the app is currently fetching projects or not
	 */
	isFetching: boolean;
	/**
	 * Whether the app has initialized projects or not
	 */
	isInitialized: boolean;
	/**
	 * Fetches projects if not already initialized
	 */
	initializeProjects: () => void;
	/**
	 * The active project as defined by authenticationContext.defaultSite
	 */
	activeProject: UserProject | undefined;
	/**
	 * All projects apart from the active project
	 */
	alternativeProjects: UserProject[];
	/**
	 * Object mapping from tenantId to projectSourceId
	 */
	tenantProjectSourceMapping: {
		[k: string]: string;
	};
	/**
	 * Switches and reroutes to project based on projectSourceId
	 * @param projectSourceId
	 * @returns
	 */
	switchProject: (projectSourceId: string) => void;
	/**
	 * Fires project/inject action. Meant to cache data while user context is refetched.
	 * @param projectSourceId
	 * @returns
	 */
	injectProjectBySourceId: (projectSourceId: string) => void;
	/**
	 * List of all available projects for a user
	 */
	projects: UserProject[];
	/**
	 * Cursor for pagination
	 */
	cursor: string | null;
	/**
	 * Error if fetching projects failed
	 */
	error: AxiosError | null;
}

type ProjectSourceId = string;
type TenantId = string;

export const useProjects = (): UseProjects => {
	const dispatch = useDispatch();
	const history = useHistory();
	const {
		isFetching,
		isInitialized,
		cursor,
		list: projects,
		error,
	} = useSelector(selectProjects);
	const teams = useSelector(selectTeams);
	const { defaultSiteProjectId } = useSelector(selectAuthenticationContext);
	const productPickerEnabled = useIsFeatureEnabled(
		'info-360-platform-product-picker',
	);

	// TODO Figure out a better way to determine login type
	const isAuth0 =
		localStorage.getItem(AUTHENTICATION_PROVIDER) == AuthType.Auth0;

	const tenantProjectSourceMapping = useMemo(() => {
		const mappingSet = new Set<[TenantId, ProjectSourceId]>();

		teams.forEach(team => {
			if (team.projectSourceId)
				mappingSet.add([team.tenantId, team.projectSourceId]);
		});

		// Convert the Set to an object before returning
		return Object.fromEntries(mappingSet);
	}, [teams]);

	const findActiveProject = useCallback(
		(project: UserProject) => {
			return project.id === defaultSiteProjectId;
		},
		[defaultSiteProjectId],
	);

	const findProjectBySourceId = useCallback(
		(projectSourceId: string) => (project: UserProject) =>
			project.sourceId === projectSourceId,
		[],
	);

	const switchProject = (projectSourceId: string) => {
		const project = projects.find(findProjectBySourceId(projectSourceId));
		if (project) {
			history.push(`/projects/${project.sourceId}`);
		} else {
			console.warn('project with matching id does not exist');
		}
	};

	const injectProjectBySourceId = useCallback(
		(projectSourceId: string) => {
			const project = projects.find(
				findProjectBySourceId(projectSourceId),
			);
			if (!project) return;
			// setTenantId(project.organizationId);
			dispatch(injectProject(project));
		},
		[projects.length],
	);

	const initializeProjects = () => {
		// Projects are not used on Auth0 and only when product picker is enabled
		if (isAuth0) return;
		// Ignore initialization attempts while fetching
		if (isInitialized || isFetching) return;
		dispatch(getProjects({ initializing: true }));
	};

	const activeProject: UserProject | undefined = useMemo(() => {
		//@ts-ignore
		return projects.find<UserProject>(findActiveProject) || projects[0];
	}, [findActiveProject, projects.length, defaultSiteProjectId]);

	const alternativeProjects: UserProject[] = projects.filter(
		project => project.id !== defaultSiteProjectId,
	);

	return {
		isFetching,
		isInitialized,
		injectProjectBySourceId,
		initializeProjects,
		activeProject,
		alternativeProjects,
		tenantProjectSourceMapping,
		switchProject,
		projects,
		cursor,
		error,
	};
};
